import { useEffect, useState } from "react";
import { Plus, Trash2, Check, X, Activity } from "lucide-react";
import type { WebhookResponse } from "@wizard-sounds/shared";
import { EVENT_TYPES } from "@wizard-sounds/shared";
import { api } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/utils";

export function AdminWebhooks() {
  const [webhooks, setWebhooks] = useState<WebhookResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchWebhooks = async () => {
    setLoading(true);
    try {
      const data = await api.admin.webhooks.list();
      setWebhooks(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const handleCreate = async () => {
    if (!name || !url || eventTypes.length === 0) return;
    await api.admin.webhooks.create({ name, url, event_types: eventTypes });
    setName("");
    setUrl("");
    setEventTypes([]);
    setShowCreate(false);
    fetchWebhooks();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this webhook?")) return;
    await api.admin.webhooks.delete(id);
    fetchWebhooks();
  };

  const toggleEventType = (type: string) => {
    setEventTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-spark border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold tracking-display">Webhooks</h1>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1.5 rounded-xl bg-spark px-3 py-1.5 text-sm font-medium text-ink hover:bg-spark-soft"
        >
          <Plus className="h-4 w-4" />
          Add webhook
        </button>
      </div>

      {showCreate && (
        <div className="mt-4 rounded-[18px] border border-mist bg-slate p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My webhook"
                className="mt-1 w-full rounded-xl border border-mist bg-ink px-3 py-1.5 text-sm text-parchment placeholder:text-fog outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
                URL
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                className="mt-1 w-full rounded-xl border border-mist bg-ink px-3 py-1.5 text-sm text-parchment placeholder:text-fog outline-none focus:border-spark focus:ring-2 focus:ring-spark/20"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="font-mono text-[10px] uppercase tracking-wider text-fog">
                Event types
              </label>
              <div className="mt-1 flex gap-2">
                {EVENT_TYPES.map((type) => (
                  <button
                    key={type}
                    onClick={() => toggleEventType(type)}
                    className={cn(
                      "rounded-xl px-3 py-1 font-mono text-xs transition-colors",
                      eventTypes.includes(type)
                        ? "bg-spark text-ink"
                        : "bg-slate-raised text-fog hover:text-parchment",
                    )}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2 flex gap-2">
              <button
                onClick={handleCreate}
                className="rounded-xl bg-spark px-4 py-1.5 text-sm font-medium text-ink hover:bg-spark-soft"
              >
                Create
              </button>
              <button
                onClick={() => setShowCreate(false)}
                className="rounded-xl bg-slate-raised px-4 py-1.5 text-sm text-fog hover:text-parchment"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {webhooks.map((wh) => (
          <div
            key={wh.id}
            className="rounded-[18px] border border-mist bg-slate p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-parchment">{wh.name}</h3>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 font-mono text-[10px]",
                      wh.is_active
                        ? "bg-moss/20 text-moss"
                        : "bg-slate-raised text-fog",
                    )}
                  >
                    {wh.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="mt-0.5 font-mono text-[11px] text-fog truncate max-w-md">
                  {wh.url}
                </p>
                <div className="mt-1 flex gap-1">
                  {JSON.parse(wh.event_types).map((type: string) => (
                    <span
                      key={type}
                      className="rounded-full bg-slate-raised px-1.5 py-0.5 font-mono text-[10px] text-fog"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    setExpandedId(expandedId === wh.id ? null : wh.id)
                  }
                  className="rounded-lg p-1.5 text-fog hover:bg-slate-raised hover:text-parchment"
                  title="View deliveries"
                >
                  <Activity className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(wh.id)}
                  className="rounded-lg p-1.5 text-ember hover:bg-ember/10"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {expandedId === wh.id && (
              <div className="mt-3 border-t border-mist pt-3">
                <h4 className="font-mono text-[10px] uppercase tracking-wider text-fog">
                  Recent deliveries
                </h4>
                {wh.recent_deliveries.length === 0 ? (
                  <p className="mt-2 text-xs text-fog">
                    No deliveries yet
                  </p>
                ) : (
                  <div className="mt-2 space-y-1">
                    {wh.recent_deliveries.map((d) => (
                      <div
                        key={d.id}
                        className="flex items-center gap-2 font-mono text-xs"
                      >
                        {d.status_code && d.status_code >= 200 && d.status_code < 300 ? (
                          <Check className="h-3 w-3 text-moss" />
                        ) : (
                          <X className="h-3 w-3 text-ember" />
                        )}
                        <span className="text-fog">
                          {d.event_type}
                        </span>
                        <span className="text-parchment">
                          {d.status_code ?? "failed"}
                        </span>
                        <span className="ml-auto text-fog">
                          {formatRelativeTime(d.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {webhooks.length === 0 && (
          <div className="py-12 text-center text-fog">
            <p>No webhooks configured</p>
            <p className="mt-1 text-sm">
              Add a webhook to get notified when sounds are played or published
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
